import axios from "axios";
import React from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import { EyeIcon, EyeSlashIcon } from "@heroicons/react/20/solid";
import lock_img from "../../../assets/password.svg";
import AuthShell, { AuthHeading, PrimaryAuthButton } from "../../../components/auth/AuthShell";
import { TwInputGroup, TwMainLayout } from "../../../components/tailwind";
import { useLocalStorage } from "../../../hooks";
import { useTenant } from "../../../provider/TenantProvider";
import * as userActions from "../../../redux/reducers/userReducer";
import { getUrlForUsers } from "../../../utils/confighelpers";
import { defaultDomainList } from "../../../utils/constants";
import { StoreButtons } from "./GetAppPage";

/**
 * /members/activate?t=<token> — the "Activate your account" button in the club's welcome email.
 * Web first: the member a club added (username = mobile number, not activated) chooses a password here
 * and is signed in. Then they can download the app and sign in there with the same mobile number + password.
 *
 * A trial guest the club added to its members gets the same link from a "Thanks for trying … Join <club> as a member"
 * email (fromTrial): the page says "Join <club> as a member" and ends with "Buy credits" (/members/webshop).
 *
 *  POST users/checkMemberActivation {token, appUrl}  -> name, mobile, email, status ready|active, QR for /get-app, fromTrial
 *  PUT  users/completeMemberActivation {token, password} -> the same login data as loginUser (signed in)
 */

const MIN_PASSWORD = 8;

const apiMessage = (res, fallback) => (res?.data && typeof res.data.message === "string" && res.data.message) || fallback;

function InfoRow({ icon, label, value }) {
  return (
    <div className="flex items-center gap-3 py-1">
      <span className="flex h-5 w-5 shrink-0 items-center justify-center text-brown-coffee" aria-hidden="true">
        {icon}
      </span>
      <span className="text-sm text-gray-600">{label}</span>
      <span className="ml-auto min-w-0 truncate text-right text-sm font-extrabold text-gray-900">{value}</span>
    </div>
  );
}

const PhoneGlyph = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="6" y="2" width="12" height="20" rx="3" />
    <path d="M11 18h2" />
  </svg>
);
const MailGlyph = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="m3 7 9 6 9-6" />
  </svg>
);

export default function ActivateAccountPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { tenant, tenantData } = useTenant();
  const club = defaultDomainList.indexOf(tenant) < 0 && tenantData?.business_details ? tenantData.business_details : null;
  const [, setUserToken] = useLocalStorage("userToken");
  const token = React.useMemo(() => new URLSearchParams(window.location.search).get("t") || "", []);

  // loading | ready | active | error | done
  const [phase, setPhase] = React.useState(token ? "loading" : "error");
  const [info, setInfo] = React.useState(null);
  const [error, setError] = React.useState(token ? "" : "This activation link isn't complete. Use the button in your welcome email, or ask your club to send it again.");
  const [password, setPassword] = React.useState("");
  const [repeat, setRepeat] = React.useState("");
  const [visible, setVisible] = React.useState(false);
  const [fieldErrors, setFieldErrors] = React.useState({});
  const [busy, setBusy] = React.useState(false);
  const [formError, setFormError] = React.useState("");

  const clubName = info?.club?.businessName || tenantData?.business_details?.business_detail?.businessName || tenantData?.businessName || "your club";
  const first = (info?.fullName || "").trim().split(/\s+/)[0] || "";
  const fromTrial = info?.fromTrial === true;
  const eyebrow = fromTrial ? "JOIN AS A MEMBER" : "MEMBER ACTIVATION";

  React.useEffect(() => {
    if (!token) return;
    let alive = true;
    (async () => {
      try {
        const res = await axios.post(`${getUrlForUsers()}checkMemberActivation`, { token, appUrl: `${window.location.origin}/get-app` });
        if (!alive) return;
        if (res.status === 200 && res.data?.statusCode === 200) {
          setInfo(res.data.data);
          setPhase(res.data.data?.status === "active" ? "active" : "ready");
        } else {
          setError(apiMessage(res, "This activation link isn't valid. Ask your club to send the welcome email again."));
          setPhase("error");
        }
      } catch (e) {
        if (!alive) return;
        setError(apiMessage(e?.response, "Couldn't reach the server. Please check your connection and try again."));
        setPhase("error");
      }
    })();
    return () => {
      alive = false;
    };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (password.length < MIN_PASSWORD) errs.password = `Use at least ${MIN_PASSWORD} characters.`;
    if (!repeat) errs.repeat = "Repeat your password.";
    else if (repeat !== password) errs.repeat = "The passwords don't match.";
    setFieldErrors(errs);
    setFormError("");
    if (Object.keys(errs).length) return;
    setBusy(true);
    try {
      const res = await axios.put(`${getUrlForUsers()}completeMemberActivation`, { token, password, deviceType: "Web" });
      if (res.status === 200 && res.data?.statusCode === 200 && res.data.data?.token) {
        const userData = res.data.data;
        // Signed in, the same way the sign-in page does it for members.
        setUserToken(userData.token);
        dispatch(userActions.setBusinessType("class"));
        dispatch(userActions.setUserData(userData));
        setPhase("done");
      } else {
        setFormError(apiMessage(res, "Couldn't activate your account. Please try again."));
      }
    } catch (err) {
      setFormError(apiMessage(err?.response, "Couldn't reach the server. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  const eye = (
    <button type="button" className="flex" aria-label={visible ? "Hide password" : "Show password"} onClick={() => setVisible((v) => !v)}>
      {visible ? <EyeIcon className="size-5" /> : <EyeSlashIcon className="size-5" />}
    </button>
  );

  return (
    <TwMainLayout fullScreen={true} checkSession={false} userType="skip">
      <AuthShell club={club} brandTitle="Welcome to FitClub." brandLine="Choose a password and start booking." clubBadge={eyebrow}>
        {phase === "loading" && (
          <div className="flex flex-col gap-4" aria-busy="true" aria-label="Checking your link">
            <div className="h-10 w-3/4 animate-pulse rounded-xl bg-[#F3EEF5]" />
            <div className="h-24 animate-pulse rounded-2xl bg-[#F3EEF5]" />
            <div className="h-14 animate-pulse rounded-2xl bg-[#F3EEF5]" />
          </div>
        )}

        {phase === "error" && (
          <div className="flex flex-col gap-6">
            <AuthHeading eyebrow={eyebrow} title="This link doesn't work">
              {error}
            </AuthHeading>
            <p className="text-sm text-gray-600">Already activated? Sign in with your mobile number and password.</p>
            <PrimaryAuthButton type="button" onClick={() => navigate("/")}>
              Go to sign in
            </PrimaryAuthButton>
          </div>
        )}

        {phase === "active" && (
          <div className="flex flex-col gap-6">
            <AuthHeading eyebrow={eyebrow} title="Your account is already active">
              Sign in with your mobile number {info?.username ? <b className="text-gray-900">{info.username}</b> : null} and your password. Forgot it? Use
              "Forgot password" on the sign-in page.
            </AuthHeading>
            <PrimaryAuthButton type="button" onClick={() => navigate("/")}>
              Go to sign in
            </PrimaryAuthButton>
          </div>
        )}

        {phase === "ready" && (
          <form className="flex flex-col gap-5" onSubmit={submit} noValidate>
            {fromTrial ? (
              <AuthHeading eyebrow={eyebrow} title={first ? `Hi ${first}, join ${clubName}` : `Join ${clubName}`}>
                Thanks for trying a class with us! Choose a password to become a member, then buy credits and book your next class.
              </AuthHeading>
            ) : (
              <AuthHeading eyebrow={eyebrow} title={first ? `Hi ${first}, activate your account` : "Activate your account"}>
                This is the account {clubName} made for you. Choose a password and you're in.
              </AuthHeading>
            )}

            <div className="rounded-2xl border border-[#ECE4EF] bg-[#FCFBFD] px-4 py-3">
              {info?.username && <InfoRow icon={PhoneGlyph} label="Username (mobile)" value={info.username} />}
              {info?.email && <InfoRow icon={MailGlyph} label="Email" value={info.email} />}
            </div>

            <TwInputGroup
              label="New password"
              placeholder={`At least ${MIN_PASSWORD} characters`}
              name="password"
              type={visible ? "text" : "password"}
              autoComplete="new-password"
              value={password}
              leftIcon={<img src={lock_img} alt="" />}
              rightIcon={eye}
              onChange={(e) => setPassword(e.target.value)}
              errors={fieldErrors}
            />
            <TwInputGroup
              label="Repeat password"
              placeholder="Type it once more"
              name="repeat"
              type={visible ? "text" : "password"}
              autoComplete="new-password"
              value={repeat}
              leftIcon={<img src={lock_img} alt="" />}
              onChange={(e) => setRepeat(e.target.value)}
              errors={fieldErrors}
            />

            {formError && (
              <div role="alert" className="rounded-2xl border border-[#F5C2C7] bg-[#FDF0F1] px-4 py-3 text-sm font-semibold text-[#8A1C2B]">
                {formError}
              </div>
            )}

            <PrimaryAuthButton busy={busy}>{fromTrial ? "Join as a member" : "Activate and sign in"}</PrimaryAuthButton>
            <p className="text-[13px] leading-relaxed text-gray-500">
              Next time, sign in with your mobile number and this password.
            </p>
          </form>
        )}

        {phase === "done" && (
          <div className="flex flex-col gap-6">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#E8F7EE]" aria-hidden="true">
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#1F6B3A" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="m5 12 5 5 9-10" />
              </svg>
            </div>
            <AuthHeading title={fromTrial ? (first ? `Welcome to ${clubName}, ${first}!` : `Welcome to ${clubName}!`) : first ? `You're all set, ${first}` : "You're all set"}>
              {fromTrial
                ? `You're a member now and signed in. Buy credits or a subscription to book your next class.`
                : `Your account is active and you're signed in to ${clubName}.`}
            </AuthHeading>
            <div className="flex flex-col gap-3">
              {fromTrial ? (
                <PrimaryAuthButton type="button" onClick={() => navigate("/members/webshop")}>
                  Buy credits
                </PrimaryAuthButton>
              ) : (
                <PrimaryAuthButton type="button" onClick={() => navigate("/members/schedule")}>
                  Book your first class
                </PrimaryAuthButton>
              )}
              <button
                type="button"
                onClick={() => navigate("/members/purchases")}
                className="flex h-12 w-full items-center justify-center rounded-2xl bg-[#F3ECF5] text-[15px] font-extrabold text-brown-coffee transition-colors hover:bg-brown-coffee hover:text-white"
              >
                See my credits
              </button>
            </div>

            <section className="rounded-3xl border border-[#ECE4EF] bg-white p-5" aria-labelledby="get-app-title">
              <h2 id="get-app-title" className="text-[17px] font-black text-gray-900">
                Get the FitClub app
              </h2>
              <p className="mt-1 text-sm text-gray-600">
                Sign in with your mobile number{info?.username ? ` ${info.username}` : ""} and the password you just chose.
              </p>
              <div className="mt-4 flex flex-wrap items-center gap-5">
                {info?.appQr && (
                  <div className="hidden flex-col items-center gap-1.5 sm:flex">
                    <img src={info.appQr} alt="QR code to download the FitClub app" className="h-32 w-32 rounded-xl border border-[#ECE4EF]" />
                    <span className="text-xs font-semibold text-gray-500">Scan with your phone</span>
                  </div>
                )}
                <div className="flex flex-col gap-3">
                  <StoreButtons />
                </div>
              </div>
            </section>
          </div>
        )}
      </AuthShell>
    </TwMainLayout>
  );
}
